// Preset model: default patch, (de)serialization, and *defensive* validation.
//
// Imported JSON is untrusted — every value is clamped to its param's range,
// unknown keys are dropped, and missing keys fall back to defaults. We never
// apply raw imported values and never `eval`.

import {
  allParamIds,
  coerceValue,
  getSpec,
  type Patch,
  type ParamId,
  type PatchValue,
} from './params';

export const PRESET_VERSION = 1;

export const NUM_STEPS = 8;

/** One sequencer step. */
export interface StepState {
  on: boolean;
  /** Semitone offset from the played note, integer in [-24, 24]. */
  pitchOffset: number;
  /** Bipolar filter cutoff offset for this step, in [-1, 1]. */
  filterOffset: number;
  /** Fraction of the step the gate is held open, in [0.05, 1]. */
  gateLength: number;
}

/**
 * The persisted sequencer grid. Transport values (bpm, swing, active step
 * count) live in the param registry (`seq.*`) and reach the engine via
 * `setParam`, so they are intentionally *not* duplicated here.
 */
export interface SequencerState {
  steps: StepState[];
}

export interface Preset {
  version: number;
  name?: string;
  patch: Patch;
  sequencer: SequencerState;
}

// Step value ranges (kept here since steps are not in the param registry).
const PITCH_MIN = -24;
const PITCH_MAX = 24;
const GATE_MIN = 0.05;
const GATE_MAX = 1;

export function getDefaultPatch(): Patch {
  const patch = {} as Patch;
  for (const id of allParamIds()) {
    patch[id] = getSpec(id).default;
  }
  return patch;
}

export function defaultStep(): StepState {
  return { on: true, pitchOffset: 0, filterOffset: 0, gateLength: 0.5 };
}

export function getDefaultSequencer(): SequencerState {
  return { steps: Array.from({ length: NUM_STEPS }, defaultStep) };
}

export function getDefaultPreset(): Preset {
  return {
    version: PRESET_VERSION,
    name: 'Init',
    patch: getDefaultPatch(),
    sequencer: getDefaultSequencer(),
  };
}

const clamp = (v: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, v));

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Build a valid Patch from arbitrary input: start from defaults, then for each
 * *known* param id present in the input, coerce its value into range. Unknown
 * keys are silently dropped.
 */
export function validatePatch(raw: unknown): Patch {
  const patch = getDefaultPatch();
  if (!isObject(raw)) return patch;
  for (const id of allParamIds()) {
    if (Object.prototype.hasOwnProperty.call(raw, id)) {
      patch[id] = coerceValue(id, raw[id]);
    }
  }
  return patch;
}

function validateStep(raw: unknown): StepState {
  const d = defaultStep();
  if (!isObject(raw)) return d;
  const pitch = Math.round(
    clamp(num(raw.pitchOffset, d.pitchOffset), PITCH_MIN, PITCH_MAX),
  );
  return {
    on: typeof raw.on === 'boolean' ? raw.on : d.on,
    pitchOffset: pitch,
    filterOffset: clamp(num(raw.filterOffset, d.filterOffset), -1, 1),
    gateLength: clamp(num(raw.gateLength, d.gateLength), GATE_MIN, GATE_MAX),
  };
}

export function validateSequencer(raw: unknown): SequencerState {
  const steps = Array.from({ length: NUM_STEPS }, defaultStep);
  if (isObject(raw) && Array.isArray(raw.steps)) {
    for (let i = 0; i < NUM_STEPS; i++) {
      if (i < raw.steps.length) steps[i] = validateStep(raw.steps[i]);
    }
  }
  return { steps };
}

export function validatePreset(raw: unknown): Preset {
  const obj = isObject(raw) ? raw : {};
  const preset: Preset = {
    version: PRESET_VERSION,
    patch: validatePatch(obj.patch),
    sequencer: validateSequencer(obj.sequencer),
  };
  if (typeof obj.name === 'string') preset.name = obj.name.slice(0, 64);
  return preset;
}

export function serialize(preset: Preset): string {
  // Re-validate on the way out so an in-memory mutation can't write garbage.
  return JSON.stringify(validatePreset(preset), null, 2);
}

/**
 * Parse + validate untrusted preset JSON. Throws only when the text is not
 * valid JSON; any structurally-valid input is coerced into a safe Preset
 * (clamped values, dropped unknown keys, defaulted missing keys).
 */
export function deserialize(json: string): Preset {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error('Preset is not valid JSON');
  }
  return validatePreset(parsed);
}

// Re-exported for callers that round-trip individual values.
export type { Patch, ParamId, PatchValue };
